/**
 * 创建题集页面
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { quizApi } from '@/lib/api';
import { fromDatetimeLocal } from '@/lib/time';

import { QuizFormData, QuestionFormData, ChoiceFormData } from '@/types';

export default function QuizCreate() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<'basic' | 'questions'>('basic');

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<QuizFormData>({
    defaultValues: {
      title: '',
      description: '',
      identity_mode: 'email',
      award_limit_n: 100,
      retry_limit: 3,
      start_at: '',
      end_at: '',
      show_correct_answer_after_submit: true,
      questions: [{
        type: 'radiogroup',
        title: '',
        choices: [{ value: 'A', text: '' }, { value: 'B', text: '' }],
        correctAnswer: 'A',
        shuffleOptions: false,
        correctVideoUrl: '',
        wrongVideoUrl: '',
        posterUrl: '',
      }],
    },
  });

  const { fields: questionFields, append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: 'questions',
  });

  const createMutation = useMutation({
    mutationFn: async (data: QuizFormData) => {
      const quizId = crypto.randomUUID();
      
      const surveyJson = {
        title: data.title,
        description: data.description,
        pages: data.questions.map((q, index) => ({
          name: `page${index + 1}`,
          elements: [{
            type: q.type,
            name: `q${index + 1}`,
            title: q.title,
            choices: q.choices,
            correctAnswer: q.correctAnswer,
            shuffleOptions: q.shuffleOptions,
            correctVideoUrl: q.correctVideoUrl,
            wrongVideoUrl: q.wrongVideoUrl,
            posterUrl: q.posterUrl,
          }],
        })),
        showProgressBar: 'top',
        showQuestionNumbers: 'onPage',
      };

      return quizApi.createQuiz({
        id: quizId,
        title: data.title,
        survey_json: surveyJson,
        identity_mode: data.identity_mode,
        award_limit_n: data.award_limit_n,
        retry_limit: data.retry_limit,
        start_at: fromDatetimeLocal(data.start_at),
        end_at: fromDatetimeLocal(data.end_at),
        show_correct_answer_after_submit: data.show_correct_answer_after_submit,
      });
    },
    onSuccess: (data) => {
      toast.success('题集创建成功');
      navigate('/quizzes');
    },
    onError: (error) => {
      toast.error('创建失败');
    },
  });

  const onSubmit = (data: QuizFormData) => {
    createMutation.mutate(data);
  };

  const addQuestion = () => {
    appendQuestion({
      type: 'radiogroup',
      title: '',
      choices: [{ value: 'A', text: '' }, { value: 'B', text: '' }],
      correctAnswer: 'A',
      shuffleOptions: false,
      correctVideoUrl: '',
      wrongVideoUrl: '',
      posterUrl: '',
    });
  };

  const addChoice = (questionIndex: number) => {
    const currentChoices = getValues(`questions.${questionIndex}.choices`);
    const nextLetter = String.fromCharCode(65 + currentChoices.length); // A, B, C, D...
    setValue(`questions.${questionIndex}.choices`, [
      ...currentChoices,
      { value: nextLetter, text: '' },
    ]);
  };

  const removeChoice = (questionIndex: number, choiceIndex: number) => {
    const currentChoices = getValues(`questions.${questionIndex}.choices`);
    if (currentChoices.length <= 2) {
      toast.error('至少需要两个选项');
      return;
    }
    setValue(`questions.${questionIndex}.choices`, 
      currentChoices.filter((_, index) => index !== choiceIndex)
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/quizzes')}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          返回
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">创建题集</h1>
          <p className="text-gray-600">创建一个新的答题题集</p>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setCurrentStep('basic')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'basic'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            1. 基本信息
          </button>
          <button
            onClick={() => setCurrentStep('questions')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'questions'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            2. 题目设置
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Step */}
          {currentStep === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="md:col-span-2">
                  <label className="form-label">题集标题 *</label>
                  <input
                    type="text"
                    className="form-input"
                    {...register('title', { required: '请输入题集标题' })}
                    placeholder="输入题集标题"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="form-label">题集描述</label>
                  <textarea
                    className="form-textarea"
                    {...register('description')}
                    placeholder="输入题集描述（可选）"
                    rows={3}
                  />
                </div>

                {/* Identity Mode */}
                <div>
                  <label className="form-label">身份验证方式 *</label>
                  <select className="form-select" {...register('identity_mode', { required: true })}>
                    <option value="email">邮箱验证</option>
                    <option value="phone">手机验证</option>
                    <option value="email_phone">邮箱或手机号皆可</option>
                  </select>
                </div>

                {/* Award Limit */}
                <div>
                  <label className="form-label">获奖名额 *</label>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    {...register('award_limit_n', { 
                      required: '请设置获奖名额',
                      min: { value: 1, message: '获奖名额至少为1' }
                    })}
                    placeholder="100"
                  />
                  {errors.award_limit_n && (
                    <p className="text-red-500 text-sm mt-1">{errors.award_limit_n.message}</p>
                  )}
                </div>

                {/* Retry Limit */}
                <div>
                  <label className="form-label">允许重试次数 *</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    {...register('retry_limit', { 
                      required: '请设置重试次数',
                      min: { value: 0, message: '重试次数不能为负数' }
                    })}
                    placeholder="3"
                  />
                  {errors.retry_limit && (
                    <p className="text-red-500 text-sm mt-1">{errors.retry_limit.message}</p>
                  )}
                </div>

                {/* Start Time */}
                <div>
                  <label className="form-label">开始时间 *</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    {...register('start_at', { required: '请设置开始时间' })}
                  />
                  {errors.start_at && (
                    <p className="text-red-500 text-sm mt-1">{errors.start_at.message}</p>
                  )}
                </div>

                {/* End Time */}
                <div>
                  <label className="form-label">结束时间 *</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    {...register('end_at', { required: '请设置结束时间' })}
                  />
                  {errors.end_at && (
                    <p className="text-red-500 text-sm mt-1">{errors.end_at.message}</p>
                  )}
                </div>

                {/* Show Correct Answer Toggle */}
                <div className="md:col-span-2">
                  <label className="form-label">提交后展示正确答案</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      {...register('show_correct_answer_after_submit')}
                    />
                    <span className="text-gray-600 text-sm">
                      开启后，用户提交答卷即能查看正确答案与解析；关闭则仅显示得分。
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setCurrentStep('questions')}
                  className="btn btn-primary"
                >
                  下一步：设置题目
                </button>
              </div>
            </div>
          )}

          {/* Questions Step */}
          {currentStep === 'questions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">题目设置</h3>
                <button
                  type="button"
                  onClick={addQuestion}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  添加题目
                </button>
              </div>

              {/* Questions */}
              <div className="space-y-8">
                {questionFields.map((field, questionIndex) => (
                  <div key={field.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900">
                        题目 {questionIndex + 1}
                      </h4>
                      {questionFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuestion(questionIndex)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      {/* Question Type */}
                      <div>
                        <label className="form-label">题目类型</label>
                        <select
                          className="form-select"
                          {...register(`questions.${questionIndex}.type`)}
                        >
                          <option value="radiogroup">单选题</option>
                          <option value="checkbox">多选题</option>
                        </select>
                      </div>

                      {/* Question Title */}
                      <div>
                        <label className="form-label">题目标题 *</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register(`questions.${questionIndex}.title`, {
                            required: '请输入题目标题'
                          })}
                          placeholder="输入题目标题"
                        />
                        {errors.questions?.[questionIndex]?.title && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.questions[questionIndex]?.title?.message}
                          </p>
                        )}
                      </div>

                      {/* Video URLs */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="form-label">答对视频URL</label>
                          <input
                            type="url"
                            className="form-input"
                            {...register(`questions.${questionIndex}.correctVideoUrl`)}
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="form-label">答错视频URL</label>
                          <input
                            type="url"
                            className="form-input"
                            {...register(`questions.${questionIndex}.wrongVideoUrl`)}
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="form-label">视频封面URL</label>
                          <input
                            type="url"
                            className="form-input"
                            {...register(`questions.${questionIndex}.posterUrl`)}
                            placeholder="https://..."
                          />
                        </div>
                      </div>

                      {/* Choices */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="form-label mb-0">选项设置</label>
                          <button
                            type="button"
                            onClick={() => addChoice(questionIndex)}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <PlusIcon className="h-4 w-4" />
                            添加选项
                          </button>
                        </div>
                        
                        {watch(`questions.${questionIndex}.choices`)?.map((choice, choiceIndex) => (
                          <div key={choiceIndex} className="flex items-center gap-3 mb-3">
                            <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium">
                              {choice.value}
                            </span>
                            <input
                              type="text"
                              className="form-input flex-1"
                              {...register(`questions.${questionIndex}.choices.${choiceIndex}.text`, {
                                required: '请输入选项内容'
                              })}
                              placeholder="输入选项内容"
                            />
                            {watch(`questions.${questionIndex}.choices`)?.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeChoice(questionIndex, choiceIndex)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Correct Answer */}
                      <div>
                        <label className="form-label">正确答案 *</label>
                        {watch(`questions.${questionIndex}.type`) === 'radiogroup' ? (
                          <select
                            className="form-select"
                            {...register(`questions.${questionIndex}.correctAnswer`, {
                              required: '请选择正确答案'
                            })}
                          >
                            {watch(`questions.${questionIndex}.choices`)?.map((choice, index) => (
                              <option key={index} value={choice.value}>
                                {choice.value} - {choice.text}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="space-y-2">
                            {watch(`questions.${questionIndex}.choices`)?.map((choice, index) => (
                              <label key={index} className="flex items-center">
                                <input
                                  type="checkbox"
                                  value={choice.value}
                                  {...register(`questions.${questionIndex}.correctAnswer`)}
                                  className="mr-2"
                                />
                                {choice.value} - {choice.text}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Shuffle Options */}
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            {...register(`questions.${questionIndex}.shuffleOptions`)}
                            className="mr-2"
                          />
                          随机打乱选项顺序
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setCurrentStep('basic')}
                  className="btn btn-secondary"
                >
                  上一步：基本信息
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn btn-success flex items-center gap-2"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  {createMutation.isPending ? '创建中...' : '创建题集'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}